import { useEffect, useState, useRef, useCallback } from 'react';
import { ShieldCheck, ExternalLink, RefreshCw, ArrowLeft, X, Users, Loader2, Settings2, Smartphone, CheckCircle2, Circle, AlertCircle, LogOut, MessageCircle, Sparkles, Zap, Headphones, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useModuleStore, consumeTPAReauthAndReload } from '@/stores/module-store';
import { useAuthStore, getEffectiveUid } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { SharedAdminChooser } from '@/components/auth/shared-admin-chooser';
import { AnimatedHeight } from '@/components/ui/animated-height';
import { useOrgJoinStore, useOrgSetupGateVisible } from '@/stores/org-join-store';
import { useContainerSize } from '@/hooks/use-container-size';
import { doc, setDoc } from '@/lib/firebase/firestore';
import { db } from '@/config/firebase';
import { COLLECTIONS, API_ENDPOINTS } from '@/config/constants';
import { callFunction } from '@/lib/firebase/functions';
import { sendCustomerDetailsForUlgebraWhatsapp } from '@/lib/contact-ulgebra';
import {
    FACEBOOK_SDK_URL,
    FACEBOOK_EMBEDDED_SIGNUP_CONFIG_ID,
    WHATSAPP_EMBEDDED_SIGNUP_ENABLED,
    WHATSAPP_GRAPH_API,
    getFacebookAppId,
} from '@/modules/tpa/whatsapp/whatsapp-types';
import { proxyRequest } from '@/lib/api/proxy';

const CF_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true')
    ? 'https://fluidal-geophilous-ellyn.ngrok-free.dev/ulgebra-license/us-central1'
    : 'https://us-central1-ulgebra-license.cloudfunctions.net';

type SignupStep = 'idle' | 'saving' | 'exchanging' | 'waiting' | 'subscribing' | 'registering' | 'verifying' | 'done';

const STEP_LABELS: Record<string, string> = {
    saving: 'Saving WABA information',
    exchanging: 'Exchanging authorization token',
    waiting: 'Waiting for Facebook to process token',
    subscribing: 'Subscribing app to WABA',
    registering: 'Registering phone number',
    verifying: 'Verifying setup',
};

/**
 * TPA OAuth Auth Prompt.
 *
 * Shown when a TPA module uses OAuth-based authentication (e.g. WhatsApp/Facebook,
 * Intercom, RingCentral, Google Drive) and the user hasn't authorized yet.
 *
 * For WhatsApp: allows users to optionally set a custom Facebook App ID
 * (stored in localStorage as 'customFacebookAPPID') before authorizing.
 *
 * For WhatsApp with WABA_NOT_CONFIGURED: shows Facebook Embedded Signup flow
 * so the user can create/connect a WhatsApp Business Account via FB.login().
 *
 * For shared users (invited by an admin via Manage Users), shows SharedAdminChooser
 * so they can pick the admin's credentials instead of going through OAuth themselves.
 *
 * Self-gating: returns null when not applicable (API-key TPAs, already authed, etc.)
 */
export function TPAAuthPrompt() {
    const tpaModule = useModuleStore((s) => s.tpaModule);
    const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
    const tpaAuthChecking = useModuleStore((s) => s.tpaAuthChecking);
    const tpaLoading = useModuleStore((s) => s.tpaLoading);
    const saasAuthenticated = useModuleStore((s) => s.saasAuthenticated);
    const saasModule = useModuleStore((s) => s.saasModule);
    const checkTPAAuth = useModuleStore((s) => s.checkTPAAuth);
    const initiateTPAAuth = useModuleStore((s) => s.initiateTPAAuth);
    const getTPAAuthUrl = useModuleStore((s) => s.getTPAAuthUrl);
    const tpaAuthErrorCode = useModuleStore((s) => s.tpaAuthErrorCode);
    const tpaAuthError = useModuleStore((s) => s.tpaAuthError);

    const { isMini } = useContainerSize();

    // Measure auth dialog content and resize Pipedrive iframe to fit
    const authCardRef = useRef<HTMLDivElement>(null);
    const resizePipedrive = useCallback(() => {
      const el = authCardRef.current;
      const saas = useModuleStore.getState().saasModule;
      if (!el || !saas?.resizeToHeight) return;
      let contentHeight = 0;
      for (const child of Array.from(el.children)) {
        contentHeight += (child as HTMLElement).offsetHeight;
      }
      saas.resizeToHeight(contentHeight + 100);
    }, []);

    // Shared admin support
    const invitedAdminList = useAuthStore((s) => s.invitedAdminList);
    const fetchInvitedAdminList = useAuthStore((s) => s.fetchInvitedAdminList);
    const userEmail = useAuthStore((s) => s.user?.email);
    const signOut = useAuthStore((s) => s.signOut);
    const orgName = useOrgJoinStore((s) => s.orgName);
    const choiceDone = useOrgJoinStore((s) => s.choiceDone);
    const isFirstSetup = !orgName && !choiceDone;
    const orgGateVisible = useOrgSetupGateVisible();

    // Track whether user has clicked Authorize (show post-auth screen)
    const [authInitiated, setAuthInitiated] = useState(false);
    // Store the auth URL for "Open in new tab" fallback
    const [authUrl, setAuthUrl] = useState<string | null>(null);
    // Track whether user explicitly chose OAuth over shared admin access
    const [userChoseOAuth, setUserChoseOAuth] = useState(false);
    const [forceShowChooser, setForceShowChooser] = useState(false);
    const [chooserStep, setChooserStep] = useState<'choose' | 'create' | 'join'>('choose');
    // Custom Facebook App ID (for WhatsApp)
    const [showAppIdInput, setShowAppIdInput] = useState(false);
    const [customAppId, setCustomAppId] = useState(() => {
        try { return localStorage.getItem('customFacebookAPPID') || ''; } catch { return ''; }
    });

    // ── Embedded Signup state (WhatsApp only) ──
    // Track whether user chose to skip embedded signup and re-authorize via OAuth
    const [skipEmbeddedSignup, setSkipEmbeddedSignup] = useState(false);
    // FB SDK loaded and ready
    const [fbSdkReady, setFbSdkReady] = useState(false);
    // Session info from embedded signup message event (WABA ID + phone number ID)
    const embeddedSignupSessionRef = useRef<{ wabaId?: string; phoneNumberId?: string } | null>(null);
    const embeddedSignupCodeRef = useRef<string | null>(null);
    const [signupStep, setSignupStep] = useState<SignupStep>('idle');
    const [signupError, setSignupError] = useState<string | null>(null);
    const [signupSteps, setSignupSteps] = useState<string[]>([]);

    // ── "Contact Ulgebra to Setup" inline flow ──
    // Reveals a phone input → posts the user's details to Ulgebra's
    // customer-success WhatsApp template. Mirrors legacy
    // `sendCustomerDetailsForUlgebraWhatsapp` so the sales team gets the
    // same payload regardless of which UI the user landed on.
    const [contactOpen, setContactOpen] = useState(false);
    const [contactPhone, setContactPhone] = useState('');
    const [contactSending, setContactSending] = useState(false);
    const [contactSent, setContactSent] = useState(false);
    const [contactError, setContactError] = useState<string | null>(null);

    // Check if this TPA uses OAuth
    const oauthInfo = tpaModule?.getOAuthInfo?.() ?? null;
    const isWhatsApp = tpaModule?.id === 'whatsapp';

    // Skip saas gate for standalone TPAs (no SaaS module, e.g. whatsappforpage)
    const saasReady = saasAuthenticated || !saasModule;

    // Determine visibility
    const isVisible =
        !!tpaModule &&
        !tpaAuthenticated &&
        !tpaAuthChecking &&
        !tpaLoading &&
        saasReady &&
        !orgGateVisible &&
        !!oauthInfo;
    
    // Embedded signup should be shown for WhatsApp when WABA is not configured.
    // Do NOT show when the error is a Facebook API error (e.g. invalid token / OAuthException)
    // — in that case show the error message instead.
    const isFbApiError = tpaAuthErrorCode === 'FB_API_ERROR';
    const showEmbeddedSignup = WHATSAPP_EMBEDDED_SIGNUP_ENABLED && isWhatsApp && !tpaAuthenticated && !skipEmbeddedSignup && !isFbApiError;
    // When Embedded Signup is disabled, WhatsApp always uses the legacy auth page:
    // the user enters their own Facebook App ID and authorizes via OAuth. This must
    // also win over the Facebook-error screen — a restricted/expired app token
    // surfaces as FB_API_ERROR, and re-auth has to go through the user's own App ID.
    const legacyWhatsAppAuth = isWhatsApp && !WHATSAPP_EMBEDDED_SIGNUP_ENABLED;

    // Fetch invited admin list and org choice when prompt becomes visible
    useEffect(() => {
        if (isVisible) {
            fetchInvitedAdminList();
        }
    }, [isVisible, fetchInvitedAdminList]);

    // Listen for OAuth completion message from popup
    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type !== 'oauth-complete' || !event.data?.success) return;
            const ok = await checkTPAAuth();
            // Explicit completion point for OAuth TPAs. The credential
            // dialog — the other in-page consumer of the flag — never
            // renders for them (both call sites gate on !getOAuthInfo()),
            // so without this a Re-Authorize from Connections would leave
            // the previous account's senders and templates in place for
            // the rest of the session. No-op when the flag isn't set
            // (first-time auth) or when the check failed.
            if (ok) consumeTPAReauthAndReload();
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [checkTPAAuth]);

    // ── Load Facebook JS SDK when embedded signup is needed ──
    useEffect(() => {
        if (!showEmbeddedSignup || !isVisible) return;

        // Already loaded
        if (window.FB) {
            setFbSdkReady(true);
            return;
        }

        // Already injected (loading in progress)
        if (document.querySelector(`script[src="${FACEBOOK_SDK_URL}"]`)) {
            // Wait for it to finish loading
            const check = setInterval(() => {
                if (window.FB) {
                    setFbSdkReady(true);
                    clearInterval(check);
                }
            }, 200);
            return () => clearInterval(check);
        }

        // Set up FB init callback
        window.fbAsyncInit = function () {
            window.FB!.init({
                appId: getFacebookAppId(),
                autoLogAppEvents: true,
                xfbml: true,
                version: 'v22.0',
            });
            console.log('[Embedded Signup] FB SDK initialized');
            setFbSdkReady(true);
        };

        // Inject script tag
        const script = document.createElement('script');
        script.src = FACEBOOK_SDK_URL;
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        document.head.appendChild(script);
    }, [showEmbeddedSignup, isVisible]);

    // ── Listen for session info from Facebook Embedded Signup ──
    useEffect(() => {
        if (!showEmbeddedSignup || !isVisible) return;

        const handleMessage = (event: MessageEvent) => {
            // Facebook posts session info with WABA ID and phone number ID
            // when the user completes the embedded signup flow.
            // Data may arrive as object or stringified JSON.
            let parsed = event.data;
            if (typeof parsed === 'string') {
                try { parsed = JSON.parse(parsed); } catch { /* not JSON */ }
            }
            if (parsed?.type === 'WA_EMBEDDED_SIGNUP') {
                const data = parsed?.data || {};
                console.log('[Embedded Signup] Received session info:', JSON.stringify(data));
                embeddedSignupSessionRef.current = {
                    wabaId: data.waba_id || data.wabaId || '',
                    phoneNumberId: data.phone_number_id || data.phoneNumberId || '',
                };
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [showEmbeddedSignup, isVisible]);

    // Resize Pipedrive iframe to fit auth dialog content
    useEffect(() => {
      const el = authCardRef.current;
      if (!el || !isVisible) return;
      resizePipedrive();
      const observer = new ResizeObserver(() => resizePipedrive());
      observer.observe(el);
      return () => observer.disconnect();
    }, [resizePipedrive, isVisible, authInitiated]);

    if (!isVisible) return null;

    const tpaName = tpaModule!.name || 'your service';
    const adminListLoading = invitedAdminList === null;
    const hasSharedAdmins = invitedAdminList && Object.keys(invitedAdminList).length > 0;
    const showSharedAccess = (!userChoseOAuth && !orgName) || forceShowChooser;
    const showBackToOrg = userChoseOAuth || !!orgName;

    const handleSaveCustomAppId = () => {
        const trimmed = customAppId.trim();
        try {
            if (trimmed) {
                localStorage.setItem('customFacebookAPPID', trimmed);
            } else {
                localStorage.removeItem('customFacebookAPPID');
            }
        } catch {
            // localStorage not available
        }
        setShowAppIdInput(false);
    };

    const handleAuthorize = () => {
        // WhatsApp legacy auth: persist the user-entered Facebook App ID first so
        // the OAuth URL (getFacebookAppId → getOAuthInfo) authorizes against their
        // app. Empty input falls back to the default app.
        if (isWhatsApp) {
            try {
                const trimmed = customAppId.trim();
                if (trimmed) {
                    localStorage.setItem('customFacebookAPPID', trimmed);
                } else {
                    localStorage.removeItem('customFacebookAPPID');
                }
            } catch {
                // localStorage not available
            }
        }
        const url = initiateTPAAuth();
        setAuthUrl(url ?? getTPAAuthUrl());
        setAuthInitiated(true);
    };

    const handleOpenInNewTab = () => {
        if (authUrl) {
            window.open(authUrl, '_blank');
        }
    };

    const handleCheckAuth = async () => {
        const ok = await checkTPAAuth();
        // Same as the SaaS prompt: mirror prod's popup-triggered reload
        // (cross-origin on localhost, so this button completes the flow
        // there) — reload so the app re-initializes against the newly
        // authorized provider account.
        // KAN-135: a pending re-auth completes here too (purge + wipe flag +
        // reload); otherwise a plain reload is all that is needed.
        if (ok && !consumeTPAReauthAndReload()) window.location.reload();
    };

    const handleBack = () => {
        setAuthInitiated(false);
        setAuthUrl(null);
    };

    // ── Embedded Signup: stepped processing ──
    const processEmbeddedSignup = async () => {
        const code = embeddedSignupCodeRef.current;
        const uid = getEffectiveUid();
        const extensionName = useAppStore.getState().extensionName;

        // Wait up to 3s for WA_EMBEDDED_SIGNUP message if not yet received
        if (!embeddedSignupSessionRef.current?.wabaId) {
            console.log('[Embedded Signup] Waiting for session info (wabaId/phoneNumberId)...');
            await new Promise<void>((resolve) => {
                const check = setInterval(() => {
                    if (embeddedSignupSessionRef.current?.wabaId) {
                        clearInterval(check);
                        resolve();
                    }
                }, 200);
                setTimeout(() => { clearInterval(check); resolve(); }, 3000);
            });
        }
        const sessionInfo = embeddedSignupSessionRef.current;
        console.log('[Embedded Signup] Session info at processing time:', JSON.stringify(sessionInfo));

        if (!code || !uid || !extensionName) {
            setSignupError('Missing required information. Please try again.');
            return;
        }

        const tpaService = extensionName.includes('for')
            ? extensionName.split('for')[0]
            : extensionName;

        const hasWabaInfo = !!sessionInfo?.wabaId && !!sessionInfo?.phoneNumberId;
        const steps = hasWabaInfo
            ? ['saving', 'exchanging', 'waiting', 'subscribing', 'registering', 'verifying']
            : ['saving', 'exchanging', 'verifying'];
        setSignupSteps(steps);

        try {
            // Step 1: Save WABA info to Firestore
            setSignupStep('saving');
            setSignupError(null);
            // Always store WABA info under whatsappforpage — all WhatsApp apps share this token path
            const tokenDocRef = doc(
                db, COLLECTIONS.USERS, uid,
                'extensions', 'whatsappforpage', 'tokens', 'tpa',
            );
            await setDoc(tokenDocRef, {
                wabaId: sessionInfo?.wabaId || '',
                phoneNumberId: sessionInfo?.phoneNumberId || '',
            }, { merge: true });

            // Step 2: Exchange code for access token via CF
            setSignupStep('exchanging');
            const appId = getFacebookAppId();
            // For embedded signup (FB.login JS SDK), redirect_uri must match
            // the page URL where FB.login() was called, not the OAuth callback URL.
            const redirectUri = window.location.origin + window.location.pathname;
            const cfUrl = `${CF_BASE}/new_oauthCallbackFacebookLogin?client_id=${encodeURIComponent(appId)}&appCode=whatsappforpage&uid=${encodeURIComponent(uid)}&code=${encodeURIComponent(code)}&state=${encodeURIComponent(`${uid}:::whatsappforpage`)}&redirect_uri=${encodeURIComponent(redirectUri)}&format=json`;
            await fetch(cfUrl, { mode: 'no-cors' });

            if (hasWabaInfo) {
                // Step 3: Wait for Facebook to process the token before calling APIs
                setSignupStep('waiting');
                await new Promise((resolve) => setTimeout(resolve, 5000));

                // Step 4: Subscribe app to WABA (with webhook override)
                setSignupStep('subscribing');
                const tokenResult = await callFunction<
                    { appCode: string; action: string; data: { userId: string } },
                    { data: string; message: string }
                >(API_ENDPOINTS.SERVICE_API, {
                    appCode: extensionName,
                    action: 'GET_ENCRYPTED_WABA_VERIFY_TOKEN',
                    data: { userId: uid },
                });
                const verifyToken = tokenResult?.data || '';
                const webhookUrl = `https://api.ulgebra.com/v1/WhatsAppWebhooks?a=whatsappforpage&u=${encodeURIComponent(uid)}`;
                await proxyRequest({
                    appCode: extensionName,
                    url: `${WHATSAPP_GRAPH_API}/${sessionInfo!.wabaId}/subscribed_apps`,
                    method: 'POST',
                    data: {
                        override_callback_uri: webhookUrl,
                        verify_token: verifyToken,
                    },
                    TPAService: tpaService,
                });

                // Step 5: Register phone number
                setSignupStep('registering');
                await proxyRequest({
                    appCode: extensionName,
                    url: `${WHATSAPP_GRAPH_API}/${sessionInfo!.phoneNumberId}/register`,
                    method: 'POST',
                    data: { messaging_product: 'whatsapp', pin: (sessionInfo!.phoneNumberId || '').slice(-6) || '000000' },
                    TPAService: tpaService,
                });
            }

            // Step 6: Verify setup
            setSignupStep('verifying');
            await checkTPAAuth();
            // Check if auth actually succeeded (checkTPAAuth doesn't throw on failure)
            const isNowAuthed = useModuleStore.getState().tpaAuthenticated;
            if (isNowAuthed) {
                setSignupStep('done');
            } else {
                throw new Error('Setup completed but verification failed. Please try again.');
            }
        } catch (err) {
            console.error('[Embedded Signup] Step failed:', err);
            setSignupError(err instanceof Error ? err.message : 'An unexpected error occurred');
        }
    };

    const handleStartEmbeddedSignup = () => {
        if (!window.FB) {
            console.warn('[Embedded Signup] FB SDK not loaded yet');
            return;
        }

        window.FB.login(
            (response) => {
                console.log('[Embedded Signup] FB.login response:', JSON.stringify(response));
                if (response.authResponse?.code) {
                    embeddedSignupCodeRef.current = response.authResponse.code;
                    processEmbeddedSignup();
                } else {
                    console.log('[Embedded Signup] No code received, user may have cancelled');
                }
            },
            {
                config_id: FACEBOOK_EMBEDDED_SIGNUP_CONFIG_ID,
                response_type: 'code',
                override_default_response_type: true,
                extras: { version: 'v4', featureType: 'whatsapp_business_app_onboarding' },
            },
        );
    };

    const handleRetrySignup = () => {
        processEmbeddedSignup();
    };

    // ── Contact Ulgebra to Setup ──
    // Reveal the inline phone input on first click; once a number is entered,
    // post the user's details so customer-success can reach out on WhatsApp.
    const handleContactUlgebra = async () => {
        if (!contactOpen) {
            setContactOpen(true);
            setContactError(null);
            return;
        }
        const phone = contactPhone.trim();
        if (!phone) {
            setContactError('Enter your WhatsApp number');
            return;
        }
        setContactSending(true);
        setContactError(null);
        const result = await sendCustomerDetailsForUlgebraWhatsapp({ phone });
        setContactSending(false);
        if (result.ok) {
            setContactSent(true);
        } else {
            setContactError(result.error || 'Failed to contact Ulgebra');
        }
    };

    // Inline logo element for headings
    const logoInline = isWhatsApp ? (
        <img
            src="https://static.cdnlogo.com/logos/w/68/whatsapp-business.png"
            alt="WhatsApp Business"
            className="inline-block max-h-6 object-contain"
        />
    ) : tpaModule!.icon ? (
        <img
            src={tpaModule!.icon}
            alt={tpaName}
            className="inline-block max-h-6 max-w-[140px] object-contain"
        />
    ) : (
        <span>{tpaName}</span>
    );

    // ── Determine heading text ──
    const getHeadingText = () => {
        if (showSharedAccess) return 'Choose Account';
        if (showEmbeddedSignup) return <>Connect {logoInline}</>;
        if (authInitiated) return <>Waiting for {logoInline}</>;
        return <>Authorize {logoInline}</>;
    };

    return (
        <div className={isMini
            ? "fixed inset-0 z-[60] flex flex-col bg-background overflow-y-auto"
            : "fixed inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm"
        }>
            <div
                ref={authCardRef}
                className={isMini
                    ? "relative w-full flex-1 flex flex-col pt-[20%] bg-card overflow-hidden"
                    : `relative w-full ${showSharedAccess || showBackToOrg ? 'max-w-xl' : 'max-w-md'} rounded-lg border border-border bg-card shadow-lg overflow-hidden`
                }
            >
                {/* Close button — re-checks auth on close (hidden during organization setup) */}
                {!isFirstSetup && (
                    <button
                        onClick={() => checkTPAAuth()}
                        className="absolute top-3 right-3 z-10 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}

                <AnimatedHeight>
                <div key={showSharedAccess ? 'org' : 'auth'} className="animate-fade-in">
                {/* Header — hidden during organization selection */}
                {!showSharedAccess && (
                    <div className={isMini ? "px-5 pt-6 pb-3" : "px-8 pt-8 pb-4"}>
                        <div className="flex flex-col items-center text-center">
                            <div className={isMini ? "mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10" : "mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10"}>
                                {showEmbeddedSignup ? (
                                    <Smartphone className={isMini ? "h-5 w-5 text-primary" : "h-6 w-6 text-primary"} />
                                ) : (
                                    <ShieldCheck className={isMini ? "h-5 w-5 text-primary" : "h-6 w-6 text-primary"} />
                                )}
                            </div>

                            <h2 className={isMini ? "mb-1 flex items-center gap-2 text-base font-semibold text-foreground" : "mb-1 flex items-center gap-2 text-lg font-semibold text-foreground"}>
                                {getHeadingText()}
                            </h2>

                            {showEmbeddedSignup && (
                                <p className="text-sm text-muted-foreground">
                                    {signupStep === 'idle'
                                        ? 'Choose how to connect your WhatsApp Business Account.'
                                        : signupStep === 'done'
                                        ? 'Your WhatsApp Business Account has been connected successfully.'
                                        : 'Setting up your WhatsApp Business Account...'}
                                </p>
                            )}

                            {!showEmbeddedSignup && !authInitiated && (
                                <p className="text-sm text-muted-foreground">
                                    Authorize access to your {tpaName} account to continue.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Body */}
                <div className={isMini ? "px-5 pb-5 pt-3" : `px-8 pb-8 ${showSharedAccess ? 'pt-8' : ''}`}>
                    {/* Loading state while fetching admin list or checking org choice */}
                    {adminListLoading ? (
                        <div className="flex items-center justify-center py-6">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                    ) : showSharedAccess ? (
                        /* ── Shared admin chooser ── */
                        <>
                            <SharedAdminChooser
                                defaultStep={chooserStep}
                                isSetup={isFirstSetup}
                                onClose={() => {
                                    setUserChoseOAuth(true);
                                    setForceShowChooser(false);
                                }}
                            />
                        </>
                    ) : (isFbApiError && !legacyWhatsAppAuth) ? (
                        /* ── Facebook API error (e.g. OAuthException / invalid token) ── */
                        <div className="w-full space-y-3">
                            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                                <p className="text-sm text-destructive font-medium mb-1">Error from Facebook</p>
                                <p className="text-xs text-destructive/80">{tpaAuthError}</p>
                            </div>
                            <Button
                                onClick={() => { setSkipEmbeddedSignup(true); handleAuthorize(); }}
                                disabled={tpaAuthChecking}
                                className="w-full"
                            >
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Reauthorize {tpaName}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => { setSkipEmbeddedSignup(false); checkTPAAuth(); }}
                                disabled={tpaAuthChecking}
                                className="w-full"
                            >
                                {tpaAuthChecking ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking...</>
                                ) : (
                                    <><RefreshCw className="mr-2 h-4 w-4" /> Retry</>
                                )}
                            </Button>
                        </div>
                    ) : showEmbeddedSignup ? (
                        /* ── Embedded Signup screen (WhatsApp WABA not configured) ── */
                        <div className="flex flex-col items-center">
                            {signupStep === 'idle' ? (
                                <div className="w-full space-y-3">
                                    {/* Option 1: Create new WABA */}
                                    <Button
                                        onClick={handleStartEmbeddedSignup}
                                        disabled={!fbSdkReady || tpaAuthChecking}
                                        className="w-full"
                                    >
                                        {!fbSdkReady ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Loading...
                                            </>
                                        ) : (
                                            <>
                                                <Smartphone className="mr-2 h-4 w-4" />
                                                Link WhatsApp Business Account
                                            </>
                                        )}
                                    </Button>

                                    {/* "Or have us set it up" divider — frames
                                        the second path as a deliberate
                                        alternative, not a fallback. */}
                                    <div className="relative flex items-center pt-1">
                                        <div className="flex-1 border-t border-dashed border-border/60" />
                                        <span className="px-3 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground/70">
                                            or skip the setup
                                        </span>
                                        <div className="flex-1 border-t border-dashed border-border/60" />
                                    </div>

                                    {/* Option: Concierge setup — posts the
                                        same payload as the legacy
                                        `sendCustomerDetailsForUlgebraWhatsapp`
                                        but framed as "done-for-you" since
                                        embedded signup intimidates a sizeable
                                        chunk of users. */}
                                    {contactSent ? (
                                        <div className="relative overflow-hidden rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent p-4">
                                            <div className="flex items-start gap-3">
                                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
                                                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                                                        We've got it from here.
                                                    </p>
                                                    <p className="text-xs leading-relaxed text-emerald-700/80 dark:text-emerald-400/80">
                                                        A specialist will reach out on WhatsApp within the hour to wrap up your setup.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.06] via-background to-background">
                                            {/* Decorative corner glow — subtle warmth without crossing into kitsch */}
                                            <div className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full bg-primary/10 blur-2xl" aria-hidden />

                                            <div className="relative p-4 space-y-3">

                                                {/* Pitch */}
                                                <div className="space-y-1">
                                                    <h3 className="text-[15px] font-semibold leading-tight text-foreground">
                                                        Have Ulgebra set it up for you.
                                                    </h3>
                                                    <p className="text-xs leading-relaxed text-muted-foreground">
                                                        Skip Facebook's onboarding maze. A specialist gets you live, fast.
                                                    </p>
                                                </div>

                                                {/* Value props — three crisp bullets, each with a different glyph */}
                                                <ul className="space-y-1.5 pt-0.5">
                                                    <li className="flex items-start gap-2">
                                                        <Zap className="mt-[1px] h-3.5 w-3.5 shrink-0 text-primary" />
                                                        <span className="text-xs leading-snug text-foreground/80">
                                                            Most setups wrap in <span className="font-semibold text-foreground">under an hour</span>
                                                        </span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <Headphones className="mt-[1px] h-3.5 w-3.5 shrink-0 text-primary" />
                                                        <span className="text-xs leading-snug text-foreground/80">
                                                            Real human help on WhatsApp — no tickets, no queues
                                                        </span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <ShieldCheck className="mt-[1px] h-3.5 w-3.5 shrink-0 text-primary" />
                                                        <span className="text-xs leading-snug text-foreground/80">
                                                            We handle WABA, templates, and webhook wiring
                                                        </span>
                                                    </li>
                                                </ul>

                                                {/* Inline phone input — fades in once the user commits */}
                                                {contactOpen && (
                                                    <div className="space-y-1.5 pt-1 animate-fade-in">
                                                        <label className="text-[11px] font-medium text-muted-foreground">
                                                            Your WhatsApp number
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            value={contactPhone}
                                                            onChange={(e) => setContactPhone(e.target.value)}
                                                            placeholder="+1 415 555 0100"
                                                            disabled={contactSending}
                                                            autoFocus
                                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                                                        />
                                                        {contactError && (
                                                            <p className="flex items-center gap-1 text-xs text-destructive">
                                                                <AlertCircle className="h-3 w-3" />
                                                                {contactError}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}

                                                {/* CTA */}
                                                <Button
                                                    onClick={handleContactUlgebra}
                                                    disabled={contactSending}
                                                    className="group w-full bg-foreground text-background hover:bg-foreground/90"
                                                >
                                                    {contactSending ? (
                                                        <>
                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                            Sending your details...
                                                        </>
                                                    ) : contactOpen ? (
                                                        <>
                                                            <MessageCircle className="mr-2 h-4 w-4" />
                                                            Send to Ulgebra
                                                            <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MessageCircle className="mr-2 h-4 w-4" />
                                                            Contact Ulgebra to Setup
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* TODO: Re-enable after launch */}
                                    {/* <div className="relative flex items-center">
                                        <div className="flex-1 border-t border-border" />
                                        <span className="px-3 text-xs text-muted-foreground">or</span>
                                        <div className="flex-1 border-t border-border" />
                                    </div> */}
                                    {/* Option 2: Link existing / Re-authorize */}
                                    {/* <Button
                                        variant="outline"
                                        onClick={() => { setSkipEmbeddedSignup(true); handleAuthorize(); }}
                                        disabled={tpaAuthChecking}
                                        className="w-full"
                                    >
                                        <ExternalLink className="mr-2 h-4 w-4" />
                                        {tpaAuthErrorCode === 'WABA_NOT_CONFIGURED'
                                            ? 'Re-authorize Facebook'
                                            : 'Link existing WhatsApp Business Account'}
                                    </Button> */}
                                </div>
                            ) : (
                                /* ── Stepped progress UI ── */
                                <div className="w-full space-y-3">
                                    <ul className="space-y-2">
                                        {signupSteps.map((stepKey) => {
                                            const stepIndex = signupSteps.indexOf(stepKey);
                                            const activeIndex = signupSteps.indexOf(signupStep);
                                            const isDone = signupStep === 'done';
                                            const isCompleted = isDone || stepIndex < activeIndex;
                                            const isActive = !isDone && stepIndex === activeIndex;
                                            const isFailed = isActive && !!signupError;
                                            const isRunning = isActive && !signupError;

                                            return (
                                                <li key={stepKey} className="flex items-center gap-3">
                                                    {isCompleted ? (
                                                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                                                    ) : isRunning ? (
                                                        <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />
                                                    ) : isFailed ? (
                                                        <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                                                    ) : (
                                                        <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                                                    )}
                                                    <span className={`text-sm ${
                                                        isCompleted ? 'text-foreground' :
                                                        isRunning ? 'text-foreground font-medium' :
                                                        isFailed ? 'text-destructive font-medium' :
                                                        'text-muted-foreground'
                                                    }`}>
                                                        {STEP_LABELS[stepKey]}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>

                                    {signupError && (
                                        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
                                            <p className="text-xs text-destructive">{signupError}</p>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={handleRetrySignup}
                                                className="w-full"
                                            >
                                                <RefreshCw className="mr-2 h-3 w-3" />
                                                Retry
                                            </Button>
                                        </div>
                                    )}

                                    {signupStep === 'done' && (
                                        <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-3 text-center">
                                            <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                                                Setup complete!
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : !authInitiated ? (
                        /* ── Initial screen: authorize ── */
                        <div className="flex flex-col items-center">
                            {showBackToOrg && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setUserChoseOAuth(false);
                                        setChooserStep('choose');
                                        setForceShowChooser(true);
                                    }}
                                    className="mb-3 self-start flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <ArrowLeft className="h-3 w-3" />
                                    Back
                                </button>
                            )}
                            {/* Link back to shared access (if admins exist and user chose OAuth) */}
                            {hasSharedAdmins && (
                                <div className="w-full mb-4 flex items-center gap-2 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 p-3">
                                    <Users className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                                    <p className="text-xs text-blue-700 dark:text-blue-300 flex-1">
                                        You have been invited to join organization
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setUserChoseOAuth(false);
                                            setChooserStep('join');
                                            setForceShowChooser(true);
                                        }}
                                        className="shrink-0 text-xs border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                                    >
                                        Join Organization
                                    </Button>
                                </div>
                            )}

                            {legacyWhatsAppAuth ? (
                                /* ── Legacy WhatsApp auth: enter Facebook App ID, then authorize via OAuth ── */
                                <div className="w-full space-y-3">
                                    {/* Surface the prior Facebook error (e.g. restricted app /
                                        expired token) so the user knows why re-auth is needed. */}
                                    {isFbApiError && tpaAuthError && (
                                        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-left">
                                            <p className="text-xs font-medium text-destructive mb-0.5">Authorization failed</p>
                                            <p className="text-[11px] text-destructive/80">{tpaAuthError}</p>
                                        </div>
                                    )}
                                    {/* WhatsApp Business API approval note (mirrors legacy page) */}
                                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs leading-relaxed text-amber-700 dark:text-amber-300">
                                        WhatsApp Business API access requires your own approved Facebook app.{' '}
                                        <a
                                            href="https://app.ulgebra.com/inbox/whatsappforpage"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-medium underline underline-offset-2"
                                        >
                                            Contact Ulgebra
                                        </a>{' '}
                                        for instant approval.
                                    </div>

                                    {/* Facebook App ID input */}
                                    <div className="w-full space-y-1.5 text-left">
                                        <label className="text-xs font-medium text-muted-foreground">
                                            Facebook App ID
                                        </label>
                                        <input
                                            type="text"
                                            value={customAppId}
                                            onChange={(e) => setCustomAppId(e.target.value)}
                                            placeholder="Type your Facebook App ID"
                                            autoFocus
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                        />
                                        <p className="text-[11px] text-muted-foreground">
                                            Already approved? Enter your app&apos;s ID and authorize.{' '}
                                            <a
                                                href="https://developers.facebook.com/apps/"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-primary hover:underline"
                                            >
                                                Where do I find this?
                                            </a>
                                        </p>
                                    </div>

                                    {/* Authorize */}
                                    <Button
                                        onClick={handleAuthorize}
                                        disabled={tpaAuthChecking}
                                        className="w-full"
                                    >
                                        {tpaAuthChecking ? (
                                            <>
                                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                                Checking...
                                            </>
                                        ) : (
                                            <>
                                                <ExternalLink className="mr-2 h-4 w-4" />
                                                Authorize now
                                            </>
                                        )}
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    {/* Custom Facebook App ID input (WhatsApp only) */}
                                    {isWhatsApp && showAppIdInput && (
                                        <div className="mb-4 w-full rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                                            <label className="text-xs font-medium text-muted-foreground">
                                                Facebook App ID
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={customAppId}
                                                    onChange={(e) => setCustomAppId(e.target.value)}
                                                    placeholder="Enter your Facebook App ID"
                                                    className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={handleSaveCustomAppId}
                                                >
                                                    Save
                                                </Button>
                                            </div>
                                            <p className="text-[11px] text-muted-foreground">
                                                Leave empty to use the default App ID. Custom ID is saved locally.
                                            </p>
                                        </div>
                                    )}

                                    <Button
                                        onClick={handleAuthorize}
                                        disabled={tpaAuthChecking}
                                        className="w-full"
                                    >
                                        {tpaAuthChecking ? (
                                            <>
                                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                                Checking...
                                            </>
                                        ) : (
                                            <>
                                                <ExternalLink className="mr-2 h-4 w-4" />
                                                Authorize {tpaName}
                                            </>
                                        )}
                                    </Button>

                                    {/* Toggle custom App ID input (WhatsApp only) */}
                                    {isWhatsApp && !showAppIdInput && (
                                        <button
                                            onClick={() => setShowAppIdInput(true)}
                                            className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            <Settings2 className="h-3 w-3" />
                                            Use custom Facebook App ID
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        /* ── Post-auth screen: waiting for user to complete auth ── */
                        <div className="flex flex-col items-center">
                            <p className="mb-5 text-sm text-muted-foreground text-center">
                                Complete the authorization in the popup window.
                                If the popup didn&apos;t open, use the link below.
                            </p>

                            <div className="flex w-full flex-col gap-3">
                                {/* Open in new tab fallback */}
                                {authUrl && (
                                    <Button
                                        variant="outline"
                                        onClick={handleOpenInNewTab}
                                        className="w-full"
                                    >
                                        <ExternalLink className="mr-2 h-4 w-4" />
                                        Open in New Tab
                                    </Button>
                                )}

                                {/* Check authorization */}
                                <Button
                                    onClick={handleCheckAuth}
                                    disabled={tpaAuthChecking}
                                    className="w-full"
                                >
                                    {tpaAuthChecking ? (
                                        <>
                                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                            Checking...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw className="mr-2 h-4 w-4" />
                                            I&apos;ve Authorized — Refresh
                                        </>
                                    )}
                                </Button>

                                {/* Go back */}
                                <button
                                    onClick={handleBack}
                                    className="mt-1 flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <ArrowLeft className="h-3 w-3" />
                                    Back
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                </div>
                </AnimatedHeight>

                {/* Current account + sign out */}
                <div className={isMini ? "px-5 pb-4 pt-2 mt-2 flex items-center justify-between bg-muted/50 border-t border-border/50" : "px-8 pb-6 pt-3 mt-2 flex items-center justify-between rounded-b-2xl bg-muted/50 border-t border-border/50"}>
                    <span className="text-[11px] text-muted-foreground truncate">
                        {userEmail}
                    </span>
                    <button
                        onClick={() => signOut()}
                        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive transition-colors shrink-0 ml-2"
                    >
                        <LogOut className="h-3 w-3" />
                        Sign out
                    </button>
                </div>

            </div>
        </div>
    );
}
